import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, Clock, Users, MapPin, Sparkles } from "lucide-react";
import { MaskedLines, Reveal, SectionHead } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { SERVICES, waLink } from "@/lib/data";

const SpecChip = ({ icon: Icon, label }) => (
  <span className="flex items-center gap-2.5 rounded-full border-2 border-white/40 bg-white/15 px-5 py-3 text-sm font-bold text-white backdrop-blur">
    <Icon size={15} className="text-gold-light" />
    {label}
  </span>
);

const ShowAdviseur = ({ service }) => {
  const [format, setFormat] = useState("podium");
  const [gasten, setGasten] = useState(150);
  const [sfeer, setSfeer] = useState("familie");

  const duur =
    format === "mix"
      ? "2 × 30 min (close-up + podium)"
      : gasten < 80
      ? "25 – 30 min met veel interactie"
      : "35 – 45 min met de grote goocheltrucs";
  const advies =
    format === "mix"
      ? "Magie op handhoogte tijdens de receptie, gevolgd door een podiumshow voor iedereen samen."
      : gasten < 80
      ? "Een intieme podiumshow waarin de gasten van de avond zelf de sterren zijn."
      : "Een grootschalige show met goocheltrucs van dichtbij én van ver te zien.";
  const sfeerTekst =
    sfeer === "volwassenen"
      ? "Aangevuld met mentalisme en een knipoog voor volwassen publiek."
      : "Aangepast aan alle leeftijden, van klein tot groot.";

  const chipClass = (active) =>
    `joy-hover rounded-full border-2 px-5 py-2.5 text-sm font-extrabold ${
      active ? "border-gold bg-gold text-brand-dark" : "border-white/50 text-white hover:border-gold hover:text-gold-light"
    }`;

  return (
    <div className="grid gap-8 lg:grid-cols-2" data-testid="widget-show-adviseur">
      <div className="space-y-7">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-gold-light">Format</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {[
              { key: "podium", label: "Podiumshow" },
              { key: "mix", label: "Close-up + podium" },
            ].map((f) => (
              <button
                key={f.key}
                data-testid={`adviseur-format-${f.key}`}
                onClick={() => setFormat(f.key)}
                className={chipClass(format === f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-gold-light">Aantal gasten</p>
            <p className="font-display text-2xl font-black text-white" data-testid="adviseur-gasten-output">{gasten}</p>
          </div>
          <input
            type="range"
            min="20"
            max="1000"
            step="10"
            value={gasten}
            onChange={(e) => setGasten(Number(e.target.value))}
            data-testid="adviseur-gasten-slider"
            className="mt-3 w-full accent-[#FFC93C]"
          />
        </div>
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-gold-light">Publiek</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {[
              { key: "familie", label: "Familie / breed" },
              { key: "volwassenen", label: "Volwassenen" },
            ].map((s) => (
              <button
                key={s.key}
                data-testid={`adviseur-sfeer-${s.key}`}
                onClick={() => setSfeer(s.key)}
                className={chipClass(sfeer === s.key)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-col justify-between rounded-3xl border-4 border-white/60 bg-gold p-8 shadow-xl" data-testid="adviseur-result">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.25em] text-brand-dark/80">Jouw show, op maat</p>
          <p className="mt-4 font-display text-2xl font-extrabold leading-snug text-brand-dark" data-testid="adviseur-advies">{advies}</p>
          <p className="mt-3 font-bold text-brand-dark/80">{sfeerTekst}</p>
        </div>
        <div className="mt-8 space-y-4">
          <p className="flex items-center gap-2 font-black text-brand-dark">
            <Clock size={15} /> {duur}
          </p>
          <Link
            to={`/contact?service=${service.slug}`}
            data-testid="adviseur-cta"
            className="joy-hover flex items-center justify-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-black uppercase tracking-wider text-white"
          >
            Plan jouw feestje!
            <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </div>
  );
};

const TafelTimeline = () => {
  const steps = [
    { tijd: "Aankomst", titel: "Ontvangst & apéro", tekst: "Magie op handhoogte terwijl gasten aankomen — ijsbreker en sfeermaker in één." },
    { tijd: "Aan tafel", titel: "Tafelmagie per groepje", tekst: "Per 8 à 12 gasten een klein mirakel op enkele centimeters van hun ogen." },
    { tijd: "Tussen de gangen", titel: "Bruggetjes bouwen", tekst: "Gasten die elkaar net ontmoet hebben, praten er de rest van de avond over." },
    { tijd: "Slot", titel: "Laatste verrassing", tekst: "Één toost, één kaart en één onmogelijk moment om de avond af te sluiten." },
  ];
  return (
    <div className="relative ml-3 space-y-0 border-l-2 border-gold/50 pl-8" data-testid="widget-tafel-timeline">
      {steps.map((s, i) => (
        <Reveal key={s.titel} delay={i * 0.08} className="relative pb-10 last:pb-0">
          <span className="absolute -left-[42px] top-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-gold bg-brand-deep">
            <span className="h-2 w-2 rounded-full bg-gold-light" />
          </span>
          <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-gold-light">{s.tijd}</p>
          <h4 className="mt-1.5 font-display text-xl font-black text-white">{s.titel}</h4>
          <p className="mt-2 max-w-lg font-semibold leading-relaxed text-blush">{s.tekst}</p>
        </Reveal>
      ))}
    </div>
  );
};

const CircusOpties = () => {
  const [actief, setActief] = useState(0);
  const opties = [
    {
      titel: "Jongleren",
      tekst: "Jongleeracts met ballen en meer: speels, verbluffend en altijd met veel interactie — voor jong en oud.",
      tag: "Binnen & buiten",
      img: "https://images.unsplash.com/photo-1653809674969-dbba5b0707e1?crop=entropy&cs=srgb&fm=jpg&q=85",
    },
    {
      titel: "Eenwieler",
      tekst: "De act waar iedereen voor stopt: acrobatisch, grappig en soms een stukje hoger dan je denkt.",
      tag: "Binnen & buiten",
      img: "https://images.unsplash.com/photo-1508371318814-b68373377199?crop=entropy&cs=srgb&fm=jpg&q=85",
    },
    {
      titel: "Steltenlopen",
      tekst: "Wandelend op stelten tussen je gasten door: letterlijk en figuurlijk het hoogtepunt van je feest.",
      tag: "Binnen & buiten",
      img: "https://images.unsplash.com/photo-1784854132821-cccc2d9fd033?crop=entropy&cs=srgb&fm=jpg&q=85",
    },
  ];
  return (
    <div data-testid="widget-circus-opties">
      <div className="grid gap-4 md:grid-cols-3">
        {opties.map((o, i) => (
          <button
            key={o.titel}
            data-testid={`circus-optie-${i}`}
            onClick={() => setActief(i)}
            className={`joy-hover relative overflow-hidden rounded-2xl border-2 text-left transition-all duration-500 ${
              actief === i ? "border-gold shadow-[0_0_40px_-10px_rgba(255,201,60,0.6)]" : "border-white/40 hover:border-gold"
            }`}
          >
            <img src={o.img} alt={o.titel} loading="lazy" className="h-44 w-full object-cover opacity-85" />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/25 to-transparent" />
            <div className="absolute bottom-4 left-5 right-5 flex items-center justify-between">
              <span className="font-display text-lg font-black text-white">{o.titel}</span>
              <span className="rounded-full bg-gold px-3 py-1 text-xs font-black text-brand-dark">{o.tag}</span>
            </div>
          </button>
        ))}
      </div>
      <motion.div
        key={actief}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mt-6 rounded-2xl border-2 border-white/30 bg-white/10 p-6"
        data-testid="circus-optie-detail"
      >
        <p className="font-semibold leading-relaxed text-blush">{opties[actief].tekst}</p>
      </motion.div>
    </div>
  );
};

const WorkshopSamensteller = ({ service }) => {
  const skills = ["Jongleren", "Ballonnen", "Flowersticks", "Hoelahoepen"];
  const [gekozen, setGekozen] = useState(["Jongleren", "Ballonnen"]);
  const [groep, setGroep] = useState("school");

  const toggle = (s) =>
    setGekozen((g) => (g.includes(s) ? g.filter((x) => x !== s) : [...g, s]));

  const groepen = {
    school: "School & jeugd — aangepast per leeftijd, van kleuter tot secundair.",
    feest: "Feest & festival — instapklar voor iedereen die wil meedoen.",
  };

  const chipClass = (active) =>
    `joy-hover rounded-full border-2 px-5 py-2.5 text-sm font-extrabold ${
      active ? "border-gold bg-gold text-brand-dark" : "border-white/50 text-white hover:border-gold hover:text-gold-light"
    }`;

  return (
    <div data-testid="widget-workshop-samensteller">
      <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-gold-light">Kies je kunsten</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {skills.map((s) => (
          <button
            key={s}
            data-testid={`workshop-skill-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            onClick={() => toggle(s)}
            className={chipClass(gekozen.includes(s))}
          >
            {s}
          </button>
        ))}
      </div>

      <p className="mt-8 text-xs font-extrabold uppercase tracking-[0.25em] text-gold-light">Voor welke groep?</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {Object.keys(groepen).map((g) => (
          <button
            key={g}
            data-testid={`workshop-groep-${g}`}
            onClick={() => setGroep(g)}
            className={chipClass(groep === g)}
          >
            {g === "bedrijf" ? "Teambuilding" : g === "school" ? "School & jeugd" : "Feest & festival"}
          </button>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-6 rounded-3xl border-4 border-white/60 bg-gold p-8" data-testid="workshop-result">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.25em] text-brand-dark/80">Jouw workshop</p>
          <p className="mt-3 font-display text-xl font-black text-brand-dark" data-testid="workshop-summary">
            {gekozen.length ? gekozen.join(" · ") : "Kies minstens één kunst"}
          </p>
          <p className="mt-2 max-w-md font-bold text-brand-dark/80">{groepen[groep]}</p>
          <p className="mt-3 flex items-center gap-2 font-black text-brand-dark">
            <Check size={14} /> Alle materiaal en begeleiding inbegrepen!
          </p>
        </div>
        <Link
          to={`/contact?service=${service.slug}`}
          data-testid="workshop-cta"
          className="joy-hover flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-black uppercase tracking-wider text-white"
        >
          Workshop aanvragen!
          <ArrowRight size={15} />
        </Link>
      </div>
    </div>
  );
};

const WIDGETS = {
  goochelshows: { titel: "Show adviseur", sub: "Stel jouw ideale show samen in drie stappen." },
  tafelgoochelaar: { titel: "Zo loopt jouw avond", sub: "De tafelgoochelaar glijdt geruiselijk door je programma." },
  circusartiest: { titel: "Kies jouw act", sub: "Drie unieke optredens, elk met eigen charmes en vereisten." },
  circusworkshops: { titel: "Stel je workshop samen", sub: "Kies de kunsten en de groep — wij regelen de rest." },
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/aanbod" replace />;

  const widget = WIDGETS[slug];
  const anderen = SERVICES.filter((s) => s.slug !== slug);

  return (
    <>
      <Seo
        title={`${service.title} huren voor elk feest | entertainmentshows.be`}
        description={`${service.tagline} Boek jouw ${service.title.toLowerCase()} via entertainmentshows.be.`}
        path={`/diensten/${service.slug}`}
      />
      <section className="relative overflow-hidden pb-16 pt-40" data-testid="service-hero">
        <div className="brand-glow pointer-events-none absolute inset-0" />
        <div className="confetti-dots pointer-events-none absolute inset-0 opacity-70" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <p className="flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.35em] text-gold-light">
              <Sparkles size={14} />
              {service.tag}
            </p>
            <MaskedLines
              as="h1"
              lines={[service.title]}
              className="mt-5 block font-display text-5xl font-black tracking-tight text-snow sm:text-6xl"
            />
            <Reveal delay={0.35} y={16}>
              <p className="mt-6 max-w-lg text-lg font-semibold leading-relaxed text-blush">{service.description}</p>
            </Reveal>
            <Reveal delay={0.5} y={14} className="mt-8 flex flex-wrap gap-3">
              <SpecChip icon={Clock} label={service.duration} />
              <SpecChip icon={Users} label={service.audience} />
              <SpecChip icon={MapPin} label={service.location} />
            </Reveal>
            <Reveal delay={0.65} y={14} className="mt-9 flex flex-wrap gap-4">
              <Link
                to={`/contact?service=${service.slug}`}
                data-testid="service-cta-offerte"
                className="joy-hover flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-black uppercase tracking-wider text-brand-dark"
              >
                Plan jouw feestje!
                <ArrowRight size={16} />
              </Link>
              <a
                href={waLink(`Hallo entertainmentshows.be! Ik heb interesse in: ${service.title}.`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="service-cta-whatsapp"
                className="joy-hover flex items-center gap-2 rounded-full border-2 border-white/70 px-8 py-4 text-sm font-extrabold uppercase tracking-wider text-white hover:bg-white hover:text-brand"
              >
                App ons direct!
              </a>
            </Reveal>
          </div>

          <Reveal delay={0.3} className="relative">
            <div className="rotate-1 overflow-hidden rounded-[2rem] border-4 border-white/80 shadow-[0_40px_100px_-30px_rgba(0,0,0,0.45)] transition-transform duration-500 hover:rotate-0">
              <img src={service.hero} alt={service.title} className="h-[420px] w-full object-cover lg:h-[500px]" loading="eager" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/70 via-transparent to-transparent" />
            </div>
            <div className="absolute -left-6 -top-6 flex h-24 w-24 animate-spin-slow items-center justify-center rounded-full border-2 border-gold-light/70 bg-white/90 backdrop-blur">
              <span className="font-script text-3xl text-brand">es</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y-4 border-gold/40 bg-cream py-20" data-testid="service-features">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHead
            eyebrow="Wat je krijgt"
            lines={[<>Altijd <em className="gold-text italic">inbegrepen</em>!</>]}
          />
          <div className="mt-12 grid gap-4 md:grid-cols-2">
            {service.features.map((f, i) => (
              <Reveal key={f} delay={i * 0.08}>
                <div className="flex items-start gap-4 rounded-2xl border-2 border-gold/40 bg-white p-5">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gold text-brand-dark">
                    <Check size={14} />
                  </span>
                  <p className="font-bold leading-relaxed text-cocoa">{f}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-deep py-20 lg:py-28" data-testid="service-widget">
        <div className="confetti-dots pointer-events-none absolute inset-0 opacity-0" />
        <div className="mx-auto max-w-7xl px-6">
          <SectionHead eyebrow="Interactief" lines={[widget.titel]} sub={widget.sub} dark />
          <Reveal delay={0.15} className="mt-12">
            {slug === "goochelshows" && <ShowAdviseur service={service} />}
            {slug === "tafelgoochelaar" && <TafelTimeline />}
            {slug === "circusartiest" && <CircusOpties />}
            {slug === "circusworkshops" && <WorkshopSamensteller service={service} />}
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-20" data-testid="service-andere">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHead eyebrow="Meer ontdekken" lines={[<>Ook <em className="gold-text italic">dit</em> kan je boeken!</>]} />
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {anderen.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.1}>
                <Link
                  to={`/diensten/${s.slug}`}
                  data-testid={`andere-dienst-${s.slug}`}
                  className="card-glow group relative block overflow-hidden rounded-3xl border-2 border-gold/50 bg-white transition-transform duration-500 hover:-rotate-1"
                >
                  <img src={s.image} alt={s.title} loading="lazy" className="h-56 w-full object-cover opacity-85 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/85 via-brand-dark/25 to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-gold-light">{s.tag}</p>
                      <h4 className="mt-1 font-display text-xl font-black text-white">{s.title}</h4>
                    </div>
                    <span className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-gold-light text-gold-light transition-all duration-300 group-hover:bg-gold group-hover:text-brand-dark">
                      <ArrowUpRight size={15} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
